import { useDebouncedLoading } from '../../hooks/useDebouncedLoading';
import { LoadingSpinner } from './LoadingSpinner';

interface LoadingContainerProps {
  isLoading: boolean;
  children: React.ReactNode;
  loadingText?: string;
  emptyState?: React.ReactNode;
  error?: string | Error | null;
  className?: string;
  minHeight?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Container component that handles loading, error, and empty states with modern debouncing
 */
export function LoadingContainer({
  isLoading,
  children,
  loadingText = 'Loading...',
  emptyState,
  error,
  className = '',
  minHeight = 'min-h-[200px]',
  size = 'md'
}: LoadingContainerProps) {
  const { isVisible } = useDebouncedLoading(isLoading, {
    showDelay: 100, // Faster for section loading
    minDisplayTime: 250,
    cooldownPeriod: 50
  });

  if (error) {
    const errorMessage = error instanceof Error ? error.message : error;
    return (
      <div className={`${minHeight} flex items-center justify-center ${className}`}>
        <div className="text-center">
          <div className="text-red-500 mb-2">
            <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (isVisible) {
    return (
      <div className={`${minHeight} flex items-center justify-center ${className}`}>
        <div className="text-center">
          <LoadingSpinner size={size} className="mx-auto mb-2" />
          <p className="text-sm text-gray-500 animate-pulse">{loadingText}</p>
        </div>
      </div>
    );
  }

  if (!children && emptyState) {
    return (
      <div className={`${minHeight} flex items-center justify-center ${className}`}>
        {emptyState}
      </div>
    );
  }

  return <div className={className}>{children}</div>;
}